'use client';

import type { ComponentPropsWithoutRef } from 'react';
import { useCallback } from 'react';
import clsx from 'clsx';
import { useRouter } from 'next/navigation';
import { useFragment } from '@apollo/client';
import { AccountLock } from '@frameio/graphql-client/generated';
import { isAccountAdobeManaged } from '@frameio/graphql-client/utils/isAccountAdobeManaged';
import { useSupportLauncherId } from '@frameio/jarvis';
import { useAccountPermissions } from '@frameio/permissions/hooks';
import { useActiveAccountId } from '@frameio/shared/contexts';
import { Box, Button2, Text, theme } from '@frameio/vapor';
import { IconLockFill, IconPartialFill } from '@frameio/vapor-icons';
import { useTrackIntercomOpened } from '../hooks/useTrackIntercomOpened';
import { getAccountLockTypeVariant } from '../utils';
import styles from './AccountLockingDialogContent.module.css';
import TEXT from './generated/TEXT/AccountLockingDialogContent';

const StyledLink = ({
  children,
  className,
  ...props
}: ComponentPropsWithoutRef<'a'>) => (
  <a {...props} className={clsx(styles.link, className)}>
    {children}
  </a>
);

TEXT.DEFINE(
  {
    /** Title when viewing a share that is unavailable */
    titleShare: 'This Share is Unavailable',
    /** Title when account is locked */
    titleLocked: 'Account Locked',
    /** Need help text with contact support link */
    needHelp: ({
      trackIntercomOpened,
      launcherId,
    }: {
      trackIntercomOpened: () => void;
      launcherId: string;
    }): React.ReactNode => (
      <>
        Need help?{' '}
        <StyledLink
          id={launcherId}
          onClick={trackIntercomOpened}
          rel="noreferrer"
        >
          Contact Support
        </StyledLink>
      </>
    ),
  },
  { StyledLink }
);

const getIcon = (isShare: boolean) => {
  const iconColor = theme.colors.icon.primary;
  return isShare ? (
    <IconPartialFill color={iconColor} />
  ) : (
    <IconLockFill color={iconColor} />
  );
};

type AccountLockingDialogContentProps = {
  isShare: boolean;
  onClose: () => void;
};

export const AccountLockingDialogContent = ({
  isShare,
  onClose,
}: AccountLockingDialogContentProps) => {
  const router = useRouter();
  const accountId = useActiveAccountId();
  const launcherId = useSupportLauncherId();
  const trackIntercomOpened = useTrackIntercomOpened();

  const { data: accountData } = useFragment({
    fragment: AccountLock,
    fragmentName: 'AccountLock',
    from: {
      __typename: 'Account',
      id: accountId,
    },
  });

  const account = accountData;

  const canManageBilling = useAccountPermissions({
    accountId: accountId ?? '',
    check: 'canManageBilling',
  });

  const { details, ctas } = getAccountLockTypeVariant({
    isShare,
    tier: account?.tier,
    delinquentAt: account?.delinquentAt,
    unpaidAt: account?.unpaidAt,
    ownerEmail: account?.owner?.email ?? '',
    canManageBilling: canManageBilling ?? false,
    managedByAdobe: isAccountAdobeManaged(account?.managedBy),
  });

  const handleOnClick = useCallback(
    (href: string) => {
      router.push(href);
      onClose();
    },
    [onClose, router]
  );

  const ctaCount = ctas.length;
  const buttonColor = (index: number) =>
    index === 1 || ctaCount === 1 ? 'primary' : 'secondary';

  const isCtaContactSupport = !!ctas.find((cta) => cta.type === 'intercom');

  const showNeedHelp = canManageBilling && !isCtaContactSupport;

  const title = isShare ? TEXT.titleShare : TEXT.titleLocked;

  return (
    <>
      <Box width="100%">{getIcon(isShare)}</Box>
      <Text color="primary" fontWeight="medium" variant="titleSmall">
        {title}
      </Text>

      {details.map((detail, index) => (
        <Text key={`detail-${index}`} color="secondary" variant="primary">
          {detail}
        </Text>
      ))}

      {canManageBilling && (
        <div className={styles.ctaContainer}>
          {ctas.map((cta, index) =>
            cta?.type === 'intercom' ? (
              <Button2
                key={cta.name}
                id={launcherId}
                onPress={trackIntercomOpened}
                variant={buttonColor(index)}
              >
                {cta.name}
              </Button2>
            ) : (
              <Button2
                key={cta.name}
                onPress={() => handleOnClick(cta.href)}
                variant={buttonColor(index)}
              >
                {cta.name}
              </Button2>
            )
          )}
        </div>
      )}
      {showNeedHelp && (
        <Text color="secondary" fontWeight="regular" variant="caption">
          {TEXT.needHelp({ trackIntercomOpened, launcherId })}
        </Text>
      )}
    </>
  );
};
